// Password validation helpers
export const validatePassword = (
  password: string
): { valid: boolean; message: string } => {
  if (password.length < 8) {
    return {
      valid: false,
      message: "Password must be at least 8 characters long",
    };
  }
  if (!/(?=.*[a-z])/.test(password)) {
    return {
      valid: false,
      message: "Password must contain at least one lowercase letter",
    };
  }
  if (!/(?=.*[A-Z])/.test(password)) {
    return {
      valid: false,
      message: "Password must contain at least one uppercase letter",
    };
  }
  if (!/(?=.*\d)/.test(password)) {
    return {
      valid: false,
      message: "Password must contain at least one number",
    };
  }
  if (!/(?=.*[@$!%*?&])/.test(password)) {
    return {
      valid: false,
      message: "Password must contain at least one special character (@$!%*?&)",
    };
  }
  return { valid: true, message: "" };
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const VERIFICATION_RESEND_COOLDOWN = 60; // seconds
export const VERIFICATION_EMAIL_VALIDITY_HOURS = 24; // hours - Firebase links last 3 days, but we consider them "recent" for 24 hours

// Storage key for tracking verification emails
const VERIFICATION_EMAIL_STORAGE_KEY = "verification_email_timestamps";

interface VerificationEmailRecord {
  email: string;
  timestamp: number;
}

/**
 * Check if a valid verification email was recently sent for an email address
 * @param email - Email address to check
 * @returns Object with isValid (boolean) and timeRemaining (number of hours remaining, or 0)
 */
export const hasValidVerificationEmail = (
  email: string
): { isValid: boolean; timeRemaining: number } => {
  try {
    const stored = localStorage.getItem(VERIFICATION_EMAIL_STORAGE_KEY);
    if (!stored) {
      return { isValid: false, timeRemaining: 0 };
    }

    const records: VerificationEmailRecord[] = JSON.parse(stored);
    const record = records.find((r) => r.email.toLowerCase() === email.toLowerCase());

    if (!record) {
      return { isValid: false, timeRemaining: 0 };
    }

    const now = Date.now();
    const elapsedHours = (now - record.timestamp) / (1000 * 60 * 60);
    const timeRemaining = VERIFICATION_EMAIL_VALIDITY_HOURS - elapsedHours;

    if (timeRemaining > 0) {
      return { isValid: true, timeRemaining: Math.ceil(timeRemaining) };
    }

    // Expired - remove from storage
    const updatedRecords = records.filter(
      (r) => r.email.toLowerCase() !== email.toLowerCase()
    );
    if (updatedRecords.length > 0) {
      localStorage.setItem(VERIFICATION_EMAIL_STORAGE_KEY, JSON.stringify(updatedRecords));
    } else {
      localStorage.removeItem(VERIFICATION_EMAIL_STORAGE_KEY);
    }

    return { isValid: false, timeRemaining: 0 };
  } catch (error) {
    console.error("Error checking verification email:", error);
    return { isValid: false, timeRemaining: 0 };
  }
};

/**
 * Record that a verification email was sent for an email address
 * @param email - Email address
 */
export const recordVerificationEmailSent = (email: string): void => {
  try {
    const stored = localStorage.getItem(VERIFICATION_EMAIL_STORAGE_KEY);
    const records: VerificationEmailRecord[] = stored ? JSON.parse(stored) : [];

    // Remove existing record for this email
    const filtered = records.filter(
      (r) => r.email.toLowerCase() !== email.toLowerCase()
    );

    // Add new record
    const newRecord: VerificationEmailRecord = {
      email: email.toLowerCase(),
      timestamp: Date.now(),
    };

    filtered.push(newRecord);

    // Clean up expired records (older than validity period)
    const now = Date.now();
    const validRecords = filtered.filter((r) => {
      const elapsedHours = (now - r.timestamp) / (1000 * 60 * 60);
      return elapsedHours < VERIFICATION_EMAIL_VALIDITY_HOURS;
    });

    if (validRecords.length > 0) {
      localStorage.setItem(VERIFICATION_EMAIL_STORAGE_KEY, JSON.stringify(validRecords));
    } else {
      localStorage.removeItem(VERIFICATION_EMAIL_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Error recording verification email:", error);
  }
};

/**
 * Clear verification email record for an email (e.g., when user verifies)
 * @param email - Email address
 */
export const clearVerificationEmailRecord = (email: string): void => {
  try {
    const stored = localStorage.getItem(VERIFICATION_EMAIL_STORAGE_KEY);
    if (!stored) return;

    const records: VerificationEmailRecord[] = JSON.parse(stored);
    const filtered = records.filter(
      (r) => r.email.toLowerCase() !== email.toLowerCase()
    );

    if (filtered.length > 0) {
      localStorage.setItem(VERIFICATION_EMAIL_STORAGE_KEY, JSON.stringify(filtered));
    } else {
      localStorage.removeItem(VERIFICATION_EMAIL_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Error clearing verification email record:", error);
  }
};
